import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchActiveAdsBySlot, trackAdClick, trackAdImpression } from '../../lib/adsService.js';
import { resolvePublicBannerForSlot } from '../../lib/publicBannerAssets.js';

const ROTATE_MS = 8000;

function AdMedia({ ad }) {
  const type = String(ad.media_type || '').toLowerCase();
  if (type === 'video') {
    return (
      <video
        src={ad.media_url}
        autoPlay
        loop
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  }
  return (
    <img
      src={ad.media_url}
      alt={ad.alt_text || ''}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  );
}

function StaticFallbackMedia({ asset }) {
  if (!asset?.src) return null;
  if (asset.kind === 'video') {
    return (
      <video
        src={asset.src}
        autoPlay
        loop
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  }
  return (
    <img
      src={asset.src}
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  );
}

export default function AdSlot({ slot }) {
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);
  const [staticFallback, setStaticFallback] = useState(null);
  const lastImpressedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setAds([]);
    setSlideIdx(0);
    setStaticFallback(null);

    Promise.all([fetchActiveAdsBySlot(slot), resolvePublicBannerForSlot(slot)]).then(([list, fallback]) => {
      if (cancelled) return;
      setAds(list || []);
      setStaticFallback(fallback);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [slot]);

  useEffect(() => {
    if (ads.length <= 1) return undefined;
    const t = setInterval(() => {
      setSlideIdx(i => (i + 1) % ads.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [ads.length]);

  const ad = ads.length ? ads[slideIdx % ads.length] : null;

  useEffect(() => {
    if (!ad?.id) return;
    if (lastImpressedRef.current === ad.id) return;
    lastImpressedRef.current = ad.id;
    trackAdImpression(ad.id);
  }, [ad?.id]);

  const openAd = targetAd => {
    if (!targetAd?.id) return;
    trackAdClick(targetAd.id);
    window.open(targetAd.target_url || '#', '_blank', 'noopener,noreferrer');
  };

  const badge = useMemo(
    () => (
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          fontSize: 10,
          color: 'rgba(240,237,232,0.4)',
          letterSpacing: '0.08em',
          padding: '3px 6px',
          borderRadius: 999,
          background: 'rgba(0,0,0,0.35)',
          border: '1px solid rgba(255,255,255,0.12)',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      >
        Publicidad
      </div>
    ),
    []
  );

  if (loading) return null;

  if (!ad) {
    if (!staticFallback?.src) return null;
    return (
      <div
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          window.open('/#', '_blank', 'noopener,noreferrer');
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          window.open('/#', '_blank', 'noopener,noreferrer');
        }}
        style={{
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          overflow: 'hidden',
          borderRadius: 8,
          position: 'relative',
        }}
      >
        <StaticFallbackMedia asset={staticFallback} />
        {badge}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          openAd(ad);
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          openAd(ad);
        }}
        style={{
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          overflow: 'hidden',
          borderRadius: 8,
          position: 'relative',
        }}
      >
        <AdMedia ad={ad} />
        {badge}
      </div>
      {ads.length > 1 ? (
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 5,
            zIndex: 2,
          }}
        >
          {ads.map((a, i) => (
            <button
              key={a.id}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={e => {
                e.stopPropagation();
                setSlideIdx(i);
              }}
              style={{
                width: i === slideIdx ? 14 : 6,
                height: 6,
                borderRadius: 4,
                border: 'none',
                padding: 0,
                background: i === slideIdx ? 'rgba(200,168,75,0.95)' : 'rgba(255,255,255,0.35)',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
